import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContactSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pe64hn2trhb35gvdpg', {
        method: 'POST',
        body: new URLSearchParams(formData as never),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      if (
        response.ok &&
        parsed?.code === 'OK' &&
        !serverMsg.toLowerCase().includes('spam') &&
        !serverMsg.toLowerCase().includes('form data is spam')
      ) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || t('contact.form.error'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('contact.form.error'));
    }
  };

  return (
    <section id="contact" className="w-full py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-sm font-semibold text-primary-600 tracking-wider uppercase">
              {t('nav.contact')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-3">
              {t('contact.title')}
            </h2>
            <p className="mt-3 text-foreground-600 max-w-xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="w-full lg:w-1/2">
              <form
                data-readdy-form
                id="contact-form-d9pe64hn2trhb35gvdpg"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200"
              >
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact.form.nameLabel')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder={t('contact.form.namePlaceholder')}
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact.form.emailLabel')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder={t('contact.form.emailPlaceholder')}
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact.form.phoneLabel')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder={t('contact.form.phonePlaceholder')}
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact.form.messageLabel')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder={t('contact.form.messagePlaceholder')}
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400 resize-none"
                    />
                    <p className="mt-1 text-xs text-foreground-400 text-right">Max. 500 Zeichen</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full px-6 py-3.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white text-sm font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {status === 'loading' ? 'Wird gesendet...' : t('contact.form.submit')}
                  </button>
                </div>

                {status === 'success' && (
                  <div className="mt-4 p-3 bg-primary-100 text-primary-800 text-sm rounded-md">
                    {t('contact.form.success')}
                  </div>
                )}
                {status === 'error' && (
                  <div className="mt-4 p-3 bg-accent-100 text-accent-800 text-sm rounded-md">
                    {errorMsg || t('contact.form.error')}
                  </div>
                )}

                <p className="mt-4 text-xs text-foreground-400 text-center">
                  {t('contact.form.privacyHint')}
                </p>
              </form>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{t('contact.info.address')}</h3>
                    <p className="mt-1 text-sm text-foreground-600">{t('contact.info.addressValue')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{t('contact.info.phone')}</h3>
                    <p className="mt-1 text-sm text-foreground-600">+49 (0) 4933 / 12345</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{t('contact.info.email')}</h3>
                    <p className="mt-1 text-sm text-foreground-600">info@haus-dornumersiel.de</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-time-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{t('contact.info.availability')}</h3>
                    <p className="mt-1 text-sm text-foreground-600">{t('contact.info.availabilityValue')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}